<p-toast position="top-right"></p-toast>
<!-- El PDF de la foto se abre aquí; «Descargar» dentro del visor lo guarda con el nombre que manda el backend. -->
<app-file-preview-dialog
  [visible]="!!pdfs.preview"
  [fileBlob]="pdfs.preview?.blob ?? null"
  [fileName]="pdfs.preview?.fileName ?? null"
  [downloadable]="true"
  (visibleChange)="$event || pdfs.closePreview()"
  (downloadRequested)="pdfs.savePreview()"
></app-file-preview-dialog>

<!-- Claves de calificación de cartera de Buró: una pastilla por clave con su cantidad; si el texto no se pudo leer, crudo.
     El separador « · » va oculto: en pantalla los separa el `gap`, y así el lector no lee «A1 ×3EX ×1». -->
<ng-template #ratingPills let-items let-raw="raw">
  <span *ngIf="items; else rawRating" class="alg-pills" role="group" aria-label="Claves de calificación de cartera de Buró">
    <ng-container *ngFor="let item of items; let last = last; trackBy: trackByIndex">
      <span class="alg-pill is-neutral" data-test="rating-chip" [attr.title]="'Texto de Buró: ' + raw"
        >{{ item.code }} ×{{ item.count }}</span
      ><span class="p-hidden-accessible" *ngIf="!last"> · </span>
    </ng-container>
  </span>
  <ng-template #rawRating
    ><span class="alg-mono">{{ raw ?? '—' }}</span></ng-template
  >
</ng-template>

<section class="snapshot-tab" aria-label="Buró y deuda">
  <app-snapshot-state *ngIf="envelopeState; else withSnapshot" [state]="envelopeState" (retry)="retry()"></app-snapshot-state>

  <ng-template #withSnapshot>
    <article class="alg-card" *ngIf="view?.snapshot as snapshot">
      <!-- ─── Encabezado: reporte, autorización, revisión, PDF y anexo tardío ─── -->
      <header class="alg-card__header">
        <span class="alg-card__icon"><i class="fa-solid fa-building-columns" aria-hidden="true"></i></span>
        <div class="alg-card__titles">
          <h2>Buró de Crédito</h2>
          <p>
            Buró Empresas<ng-container *ngIf="report">
              · reporte del <strong>{{ shortDate(report.createdAt.slice(0, 10)) }}</strong></ng-container
            ><span *ngIf="report?.authorization?.authorizedUntil" data-test="authorization">
              · autorización vigente al {{ shortDate(report?.authorization?.authorizedUntil) }}</span
            >
          </p>
        </div>
        <span class="alg-pill is-neutral" *ngIf="snapshot.viewedRevision" data-test="snapshot-revision"
          >Foto · revisión {{ snapshot.viewedRevision }}</span
        >
        <ng-container *ngIf="hasBureauPdf">
          <span class="alg-file-note" *ngIf="fileNote('bureau') as note" data-test="pdf-bureau-note">{{ note }}</span>
          <app-snapshot-pdf-button
            *ngIf="offersFile('bureau')"
            data-test="pdf-bureau"
            subject="el reporte de Buró de Crédito"
            [status]="fileStatus('bureau')"
            [opening]="downloading === 'bureau'"
            (open)="openFile('bureau')"
          ></app-snapshot-pdf-button>
        </ng-container>
        <!-- Anexo tardío de Buró y BIL: solo LEE en Syntage, no pide reporte nuevo ni consume extracciones. -->
        <button
          *ngIf="canAnnex"
          type="button"
          class="alg-btn"
          data-test="annex-bureau"
          [disabled]="annexing"
          [attr.aria-busy]="annexing || null"
          (click)="searchBureau()"
        >
          <i class="fa-solid" [ngClass]="annexing ? 'fa-circle-notch fa-spin' : 'fa-magnifying-glass'" aria-hidden="true"></i>Buscar reporte
          en Syntage
        </button>
      </header>

      <!-- ─── Avisos de la foto: una línea entre el encabezado y el cuerpo ─── -->
      <app-snapshot-state
        *ngIf="snapshot.reconstructed"
        state="reconstructed"
        [builtAt]="snapshot.reconstructedAt"
        [analyzedAt]="snapshot.originalVerifiedAt ?? analyzedAt"
      ></app-snapshot-state>
      <app-snapshot-state *ngIf="snapshot.pinned" state="pinned"></app-snapshot-state>
      <p class="alg-notice is-info" *ngFor="let annex of bureauAnnexes; trackBy: trackByIndex" data-test="annex-note">
        <i class="fa-solid fa-clock-rotate-left" aria-hidden="true"></i>
        <span
          >Reporte de Buró agregado el {{ annex.appendedAt | mxDate }} por {{ annex.appendedByName
          }}<ng-container *ngIf="annex.reportDate"> (reporte del {{ annex.reportDate | mxDate }})</ng-container>. Lo anterior se conserva en
          el historial del análisis.</span
        >
      </p>
      <p class="alg-notice is-neutral" *ngIf="report?.truncated" data-test="truncated-note">
        <i class="fa-solid fa-scissors" aria-hidden="true"></i>
        <span
          >Para no pasar del tamaño máximo de la foto se omitieron {{ report?.omittedClosedAccounts ?? 0 }} cuentas pagadas antiguas.</span
        >
      </p>

      <div class="alg-card__body">
        <div class="alg-card__stack" data-test="bureau-report">
          <app-snapshot-state
            *ngIf="sectionState(bureauSection); else bureauContent"
            [compact]="true"
            [state]="sectionState(bureauSection)!"
            [closesAt]="snapshot.windowEndsAt"
            [reason]="bureauSection?.reason"
          ></app-snapshot-state>

          <ng-template #bureauContent>
            <ng-container *ngIf="report">
              <!-- ─── Franja de KPIs: el veredicto del reporte ─── -->
              <div class="alg-kpis">
                <div class="alg-kpi" data-test="bureau-score">
                  <span class="alg-kpi__label">Score PyME</span>
                  <span class="alg-kpi__value" data-test="bureau-score-value"
                    >{{ report.score ?? '—' }}<span class="alg-kpi__unit" *ngIf="report.score !== null"> / {{ pymeRange.max }}</span></span
                  >
                  <!-- Barra en ámbar constante, como el prototipo: ubica el score en su escala sin afirmar si es bueno o malo. -->
                  <div
                    class="score-meter"
                    *ngIf="scorePercent !== null"
                    role="meter"
                    data-test="bureau-score-meter"
                    [attr.aria-label]="'Score PyME en su escala de ' + pymeRange.min + ' a ' + pymeRange.max"
                    [attr.aria-valuemin]="pymeRange.min"
                    [attr.aria-valuemax]="pymeRange.max"
                    [attr.aria-valuenow]="report.score"
                  >
                    <span class="score-fill" data-test="bureau-score-fill" [style.width.%]="scorePercent"></span>
                  </div>
                  <!-- Buró no publica el catálogo de razones: se muestran como código, dicho como tal. -->
                  <span class="alg-kpi__sub" *ngIf="report.scoreDetail[0]?.reasonCodes?.length"
                    >Razones de Buró
                    <span class="alg-mono" role="group" aria-label="Códigos de razón de Buró del score"
                      ><ng-container *ngFor="let code of report.scoreDetail[0]?.reasonCodes ?? []; let last = last; trackBy: trackByIndex"
                        ><span data-test="bureau-reason" [attr.title]="'Código de razón de Buró ' + code">{{ code }}</span
                        ><ng-container *ngIf="!last"> · </ng-container></ng-container
                      ></span
                    ></span
                  >
                </div>
                <div class="alg-kpi" data-test="on-time">
                  <span class="alg-kpi__label">Pagos a tiempo · 12 meses</span>
                  <span class="alg-kpi__value">{{ onTime.bank !== null ? onTime.bank + ' %' : '—' }}</span>
                  <span class="alg-kpi__sub"
                    >bancos · <strong>{{ onTime.nonBank !== null ? onTime.nonBank + ' %' : '—' }}</strong> no bancos</span
                  >
                </div>
                <div class="alg-kpi" data-test="current-balance">
                  <span class="alg-kpi__label">Saldo vigente</span>
                  <span class="alg-kpi__value" [attr.title]="money(currentBalance)">{{ compact(currentBalance) }}</span>
                  <span class="alg-kpi__sub"
                    ><strong>{{ openAccounts.length }}</strong> {{ openAccounts.length === 1 ? 'cuenta activa' : 'cuentas activas' }}</span
                  >
                </div>
                <div class="alg-kpi" data-test="overdue-balance">
                  <span class="alg-kpi__label">Saldo vencido</span>
                  <span class="alg-kpi__value" [class.is-bad]="overdueBalance > 0" [attr.title]="money(overdueBalance)">{{
                    compact(overdueBalance)
                  }}</span>
                  <span class="alg-kpi__sub">{{ overdueBalance > 0 ? 'en cuentas activas' : 'sin atraso en cuentas activas' }}</span>
                </div>
                <div class="alg-kpi" *ngIf="report.portfolioRating" data-test="portfolio-rating">
                  <span class="alg-kpi__label">Calificación de cartera</span>
                  <span class="portfolio-pills">
                    <ng-container
                      *ngTemplateOutlet="ratingPills; context: { $implicit: portfolioRating, raw: report.portfolioRating }"
                    ></ng-container>
                  </span>
                  <span class="alg-kpi__sub">claves de Buró, último mes</span>
                </div>
              </div>

              <!-- ─── Franja secundaria: consultas y vencido por antigüedad ─── -->
              <div class="alg-strip bureau-strip" data-test="inquiries">
                <div class="alg-strip__cell bureau-strip__head">
                  <span class="alg-strip__title">Consultas a Buró</span>
                  <span class="alg-strip__hint">financieras · comerciales</span>
                </div>
                <div class="alg-strip__cell" *ngFor="let row of inquiries; trackBy: trackByIndex" data-test="inquiry">
                  <div class="alg-strip__label">{{ row.label }}</div>
                  <div class="alg-strip__value">
                    <span data-test="inquiry-financial">{{ row.financial ?? '—' }}</span>
                    <span class="bureau-strip__second"
                      ><span class="p-hidden-accessible"> financieras,</span> ·
                      <span data-test="inquiry-commercial">{{ row.commercial ?? '—' }}</span
                      ><span class="p-hidden-accessible"> comerciales</span></span
                    >
                  </div>
                </div>
                <div class="alg-strip__cell bureau-aging" data-test="aging">
                  <div class="bureau-aging__head">
                    <span>Vencido por antigüedad</span>
                    <span>1–29 · 30–59 · 60–89 · 90–119 · 120–179 · 180+</span>
                  </div>
                  <div class="bureau-aging__bars" aria-hidden="true">
                    <span
                      *ngFor="let bucket of aging; trackBy: trackByIndex"
                      class="bureau-aging__bar"
                      data-test="aging-bucket"
                      [class.has-amount]="bucket.amount > 0"
                      [style.opacity]="bucket.amount > 0 ? agingOpacity(bucket.share) : null"
                      [attr.title]="bucket.label + ': ' + money(bucket.amount)"
                    ></span>
                  </div>
                  <span class="bureau-aging__sum" data-test="aging-summary">{{ agingSummary }}</span>
                </div>
              </div>

              <!-- ─── Segmentado del detalle ─── -->
              <div class="alg-controls">
                <div class="alg-seg" role="tablist" aria-label="Detalle del reporte de Buró">
                  <button
                    *ngFor="let item of segments; trackBy: trackByIndex"
                    type="button"
                    role="tab"
                    [attr.data-test]="'segment-' + item.key"
                    [attr.id]="'bureau-tab-' + item.key"
                    [attr.aria-selected]="segment === item.key"
                    aria-controls="bureau-segment-panel"
                    [class.is-on]="segment === item.key"
                    (click)="setSegment(item.key)"
                  >
                    {{ item.label }}<span class="alg-seg__n"><span class="p-hidden-accessible">: </span>{{ item.count }}</span>
                  </button>
                </div>
                <div class="alg-chips" *ngIf="segment === 'financial'" role="group" aria-label="Filtrar cuentas financieras">
                  <button
                    type="button"
                    class="alg-chip"
                    data-test="filter-open"
                    [class.is-on]="accountFilter === 'open'"
                    [attr.aria-pressed]="accountFilter === 'open'"
                    (click)="setAccountFilter('open')"
                  >
                    Activas<span class="alg-chip__n"><span class="p-hidden-accessible">: </span>{{ openAccounts.length }}</span>
                  </button>
                  <button
                    type="button"
                    class="alg-chip"
                    data-test="filter-closed"
                    [class.is-on]="accountFilter === 'closed'"
                    [attr.aria-pressed]="accountFilter === 'closed'"
                    (click)="setAccountFilter('closed')"
                  >
                    Pagadas<span class="alg-chip__n"><span class="p-hidden-accessible">: </span>{{ closedAccounts.length }}</span>
                  </button>
                </div>
                <span class="alg-aside" data-test="rating-note">Buró no manda catálogo: las claves se muestran como clave</span>
              </div>

              <div id="bureau-segment-panel" role="tabpanel" [attr.aria-labelledby]="'bureau-tab-' + segment" [ngSwitch]="segment">
                <!-- Cuentas financieras -->
                <div *ngSwitchCase="'financial'" class="alg-table-wrap is-tall">
                  <table class="alg-table accounts-table">
                    <caption class="p-hidden-accessible">
                      {{
                        accountFilter === 'open' ? 'Cuentas financieras activas' : 'Cuentas financieras pagadas'
                      }}
                    </caption>
                    <colgroup>
                      <col style="width: 44px" />
                      <col style="width: 104px" />
                      <col class="is-fill-14" />
                      <col style="width: 150px" />
                      <col style="width: 120px" />
                      <col style="width: 120px" />
                      <col class="is-fill-11" />
                      <col style="width: 70px" />
                    </colgroup>
                    <thead>
                      <tr>
                        <th scope="col"><span class="p-hidden-accessible">Detalle</span></th>
                        <th scope="col">Cuenta</th>
                        <th scope="col">Otorgante · tipo</th>
                        <th scope="col">{{ accountFilter === 'open' ? 'Apertura' : 'Apertura · cierre' }}</th>
                        <th scope="col" class="is-num">Saldo vigente</th>
                        <th scope="col" class="is-num">Vencido</th>
                        <th scope="col">Peor MOP</th>
                        <th scope="col" class="is-num">Atraso</th>
                      </tr>
                    </thead>
                    <tbody>
                      <ng-container *ngFor="let row of accounts; trackBy: trackByIndex">
                        <tr data-test="account-row" [class.is-open]="openRows.has(row.key)">
                          <td>
                            <button
                              type="button"
                              class="alg-exp"
                              data-test="account-toggle"
                              [attr.aria-expanded]="openRows.has(row.key)"
                              [attr.aria-label]="'Ver el historial de la cuenta ' + row.account.account"
                              (click)="toggleRow(row.key)"
                            >
                              <i class="fa-solid fa-chevron-right" aria-hidden="true"></i>
                            </button>
                          </td>
                          <td class="is-mono" data-test="account-number">{{ row.account.account }}</td>
                          <td>
                            {{ row.account.tipoUsuario ?? '—' }}
                            <!-- Buró manda solo la clave del tipo de crédito, sin su descripción: no se le inventa un nombre. -->
                            <span
                              class="alg-table__sub"
                              *ngIf="row.account.tipoCredito"
                              data-test="credit-type"
                              title="El reporte de Buró trae solo la clave numérica del tipo de crédito, sin su descripción"
                              >clave de tipo {{ row.account.tipoCredito }}</span
                            >
                          </td>
                          <td class="is-num-plain">
                            {{ shortDate(row.account.apertura)
                            }}<ng-container *ngIf="row.account.fechaCierre"> · {{ shortDate(row.account.fechaCierre) }}</ng-container>
                          </td>
                          <td class="is-num" data-test="account-balance">{{ money(row.account.saldoVigente ?? 0) }}</td>
                          <td class="is-num" [class.is-bad]="row.overdue > 0" data-test="account-overdue">{{ money(row.overdue) }}</td>
                          <td>
                            <!-- La pastilla lleva el código y su significado: el color nunca va solo. -->
                            <span
                              class="alg-pill"
                              data-test="mop"
                              [ngClass]="'is-' + row.mopTone"
                              [attr.title]="row.mop ? row.mop + ' · ' + row.mopLabel : null"
                              >{{ row.mop ? row.mop + ' · ' + row.mopShort : row.mopLabel
                              }}<span class="p-hidden-accessible" *ngIf="row.mop && row.mopShort !== row.mopLabel">
                                ({{ row.mopLabel }})</span
                              ></span
                            >
                          </td>
                          <td class="is-num">{{ row.account.atrasoMayor ?? '—' }}</td>
                        </tr>
                        <tr class="alg-detail" *ngIf="openRows.has(row.key)" data-test="account-detail">
                          <td colspan="8">
                            <div class="alg-detail__grid account-detail">
                              <div class="account-history">
                                <span class="alg-lbl"
                                  >Historial · {{ row.history.length }} {{ row.history.length === 1 ? 'mes' : 'meses' }}</span
                                >
                                <span class="history-squares" data-test="mop-history"
                                  ><span
                                    *ngFor="let tone of row.history; trackBy: trackByIndex"
                                    class="history-sq"
                                    [ngClass]="'is-' + tone"
                                  ></span
                                  ><span class="p-hidden-accessible">Historial: {{ row.account.historicoPagos ?? '—' }}</span></span
                                >
                                <span class="history-legend"
                                  >en el orden en que lo manda Buró · verde al corriente, ámbar 1–29 d, rojo 30+ d</span
                                >
                              </div>
                              <div>
                                <div class="alg-lbl">Crédito máximo</div>
                                <div class="alg-detail__value is-num-plain">{{ money($any(row.account['creditoMaximoUtilizado'])) }}</div>
                              </div>
                              <div>
                                <div class="alg-lbl">Último pago</div>
                                <div class="alg-detail__value">{{ shortDate(row.account.fechaUltimoPago) }}</div>
                              </div>
                              <div>
                                <div class="alg-lbl">Observación</div>
                                <div class="alg-detail__value">
                                  <span
                                    *ngIf="row.account.claveObservacion; else noObservation"
                                    class="alg-mono"
                                    title="Clave de observación de Buró"
                                    data-test="observation"
                                    ><span class="p-hidden-accessible">Clave de observación de Buró: </span
                                    >{{ row.account.claveObservacion }}</span
                                  >
                                  <ng-template #noObservation>—</ng-template>
                                </div>
                              </div>
                              <div>
                                <div class="alg-lbl">Quebrantos</div>
                                <div class="alg-detail__value">
                                  <span class="loss" *ngFor="let loss of row.losses; trackBy: trackByIndex" data-test="loss"
                                    >{{ loss.label }} {{ money(loss.amount) }}</span
                                  >
                                  <ng-container *ngIf="!row.losses.length">—</ng-container>
                                </div>
                              </div>
                            </div>
                          </td>
                        </tr>
                      </ng-container>
                    </tbody>
                  </table>
                  <div class="alg-table__foot" data-test="accounts-foot">
                    {{ accounts.length }}
                    {{
                      accountFilter === 'open'
                        ? accounts.length === 1
                          ? 'cuenta activa'
                          : 'cuentas activas'
                        : accounts.length === 1
                          ? 'cuenta pagada'
                          : 'cuentas pagadas'
                    }}
                  </div>
                </div>

                <!-- Cuentas comerciales: Buró las da en miles -->
                <div *ngSwitchCase="'commercial'" class="alg-table-wrap">
                  <table class="alg-table commercial-table">
                    <caption class="p-hidden-accessible">
                      Cuentas con empresas comerciales
                    </caption>
                    <colgroup>
                      <col />
                      <col style="width: 140px" />
                      <col style="width: 140px" />
                      <col style="width: 140px" />
                      <col style="width: 140px" />
                    </colgroup>
                    <thead>
                      <tr>
                        <!-- Buró no manda el nombre del otorgante comercial, solo su identificador dentro del reporte. -->
                        <th scope="col">Otorgante (identificador de Buró)</th>
                        <th scope="col" class="is-num">Saldo total</th>
                        <th scope="col" class="is-num">Vigente</th>
                        <th scope="col" class="is-num">Vencido</th>
                        <th scope="col">Último periodo</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr *ngFor="let account of report.commercialAccounts; trackBy: trackByIndex" data-test="commercial-row">
                        <td class="is-mono">{{ account['identificadorUsuario'] ?? '—' }}</td>
                        <td class="is-num">{{ thousandsAsPesos(account['saldoTotal']) }}</td>
                        <td class="is-num">{{ thousandsAsPesos(account['saldoVigente']) }}</td>
                        <td class="is-num" [class.is-bad]="$any(account['saldoVencido']) > 0">
                          {{ thousandsAsPesos(account['saldoVencido']) }}
                        </td>
                        <td>{{ $any(account['ultimoPeriodoActualizado']) | mxDate: 'month' }}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <!-- Calificación de cartera por mes -->
                <div *ngSwitchCase="'months'" class="alg-table-wrap">
                  <table class="alg-table months-table">
                    <caption class="p-hidden-accessible">
                      Calificación de cartera de los últimos 12 meses
                    </caption>
                    <colgroup>
                      <col style="width: 120px" />
                      <col />
                      <col style="width: 150px" />
                      <col style="width: 150px" />
                      <col style="width: 110px" />
                    </colgroup>
                    <thead>
                      <tr>
                        <th scope="col">Mes</th>
                        <th scope="col">Calificación (clave ×veces)</th>
                        <th scope="col" class="is-num">Saldo vigente</th>
                        <th scope="col" class="is-num">Máx. vencido</th>
                        <th scope="col" class="is-num">Días atraso</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr *ngFor="let row of ratingRows; trackBy: trackByIndex" data-test="rating-row">
                        <td>{{ $any(row.month['periodo']) | mxDate: 'month' }}</td>
                        <td data-test="rating-text" [attr.title]="row.raw ? 'Texto de Buró: ' + row.raw : null">
                          <ng-container *ngTemplateOutlet="ratingPills; context: { $implicit: row.items, raw: row.raw }"></ng-container>
                        </td>
                        <td class="is-num">{{ thousandsAsPesos(row.month['saldoVigente']) }}</td>
                        <td class="is-num">{{ thousandsAsPesos(row.month['maximoSaldoVencido']) }}</td>
                        <td class="is-num">{{ row.month['mayorNumeroDiasVencido'] ?? '—' }}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <!-- Alertas del Buró (previsor) -->
                <div *ngSwitchCase="'alerts'" class="alg-table-wrap">
                  <table class="alg-table alerts-table">
                    <caption class="p-hidden-accessible">
                      Alertas que reporta el Buró
                    </caption>
                    <colgroup>
                      <col style="width: 90px" />
                      <col style="width: 180px" />
                      <col style="width: 150px" />
                      <col />
                    </colgroup>
                    <thead>
                      <tr>
                        <th scope="col">Código</th>
                        <th scope="col">Reporta</th>
                        <th scope="col">Fecha</th>
                        <th scope="col">Descripción</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr *ngFor="let alert of report.alerts; trackBy: trackByIndex" data-test="alert-row">
                        <td class="is-mono">{{ alert.codigoPrevisor ?? '—' }}</td>
                        <td>{{ alert.tipoUsuarioReporta ?? '—' }}</td>
                        <td>{{ shortDate(alert.fechaMensajePrevisor) }}</td>
                        <td>{{ alert.descripcionPrevencionPrevisor ?? '—' }}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </ng-container>
          </ng-template>
        </div>

        <!-- ─── Fuera del Buró: moratorios, instituciones, RUG e Infonavit con registros ─── -->
        <div class="alg-divider"><span>Fuera del Buró</span></div>

        <!-- Dos columnas iguales y de la misma altura; si solo hay una de las dos tarjetas, ocupa todo el ancho. -->
        <div class="outside-grid" *ngIf="sourcesWithoutRecords.length < 4">
          <div
            class="alg-box moratory"
            [class.span-all]="outsideCards === 1"
            *ngIf="!sectionState(sections.moratoryInterest) && sections.moratoryInterest?.data as moratory"
            data-test="moratory"
          >
            <div class="alg-sec">
              <h3>Intereses moratorios</h3>
              <span class="alg-pill is-bad" *ngIf="moratory.summary.isRecurring">Recurrente</span>
            </div>
            <div class="moratory-figures">
              <div data-test="moratory-twelve">
                <div class="alg-kpi__label">Últimos 12 meses</div>
                <div class="moratory-value" [class.is-bad]="(moratory.summary.twelveMonthTotal ?? 0) > 0">
                  {{ money(moratory.summary.twelveMonthTotal) }}
                </div>
                <div class="alg-kpi__sub" data-test="moratory-twelve-sub">
                  {{ moratory.summary.institutionCount }} {{ moratory.summary.institutionCount === 1 ? 'institución' : 'instituciones' }} ·
                  {{ moratory.summary.periodFrom | mxDate: 'month' }} – {{ moratory.summary.periodTo | mxDate: 'month' }}
                </div>
              </div>
              <!-- El rango es el de los 3 meses que promedia, no el `periodFrom`/`periodTo` del resumen, que es de 12. -->
              <div data-test="moratory-average">
                <div class="alg-kpi__label">Promedio 3 meses</div>
                <div class="moratory-value">{{ money(moratory.summary.threeMonthAverage) }}</div>
                <div class="alg-kpi__sub" *ngIf="moratoryAverageRange as range" data-test="moratory-average-sub">
                  {{ range.from | mxDate: 'month' }} – {{ range.to | mxDate: 'month' }}
                </div>
              </div>
            </div>
            <ng-container *ngIf="moratoryBars.length">
              <div
                class="moratory-bars"
                role="img"
                [attr.aria-label]="
                  'Intereses moratorios por mes, de ' + moratoryBars[0].month + ' a ' + moratoryBars[moratoryBars.length - 1].month
                "
              >
                <span class="moratory-bars__plot">
                  <span
                    *ngFor="let bar of moratoryBars; trackBy: trackByIndex"
                    class="moratory-bar"
                    data-test="moratory-bar"
                    [class.is-zero]="bar.isZero"
                    [style.height.%]="bar.height"
                    [attr.title]="bar.month + ': ' + bar.amount"
                  ></span>
                </span>
              </div>
              <div class="moratory-axis" aria-hidden="true">
                <span>{{ moratoryBars[0].month }}</span>
                <span>{{ moratoryBars[moratoryBars.length - 1].month }}</span>
              </div>
            </ng-container>
          </div>

          <div
            class="alg-box institutions"
            [class.span-all]="outsideCards === 1"
            *ngIf="!sectionState(sections.financialInstitutions)"
            data-test="institutions"
          >
            <div class="alg-sec institutions__head">
              <h3>Instituciones financieras</h3>
              <span class="alg-pill is-neutral">{{ institutionsCount }}</span>
              <span class="institutions__total"
                >Total <strong [attr.title]="money(institutionsTotal)">{{ compact(institutionsTotal) }}</strong></span
              >
            </div>
            <table class="alg-table institutions-table">
              <caption class="p-hidden-accessible">
                Instituciones financieras con operaciones del cliente
              </caption>
              <colgroup>
                <col class="is-fill-inst" />
                <col class="is-fill-sector" />
                <col style="width: 130px" />
              </colgroup>
              <thead>
                <tr>
                  <th scope="col">Institución</th>
                  <th scope="col">Sector</th>
                  <th scope="col" class="is-num">Total</th>
                </tr>
              </thead>
              <tbody>
                <tr *ngFor="let institution of institutions; trackBy: trackByIndex" data-test="institution-row">
                  <td>
                    {{ institution.name ?? '—' }}
                    <span class="alg-table__sub alg-mono" *ngIf="institution.rfc">{{ institution.rfc }}</span>
                  </td>
                  <td class="is-secondary">
                    <span class="clamp-2" [attr.title]="institution.sector">{{ institution.sector ?? '—' }}</span>
                  </td>
                  <td class="is-num">{{ money(institution.total) }}</td>
                </tr>
              </tbody>
            </table>
            <button
              *ngIf="institutionsCount > 5"
              type="button"
              class="alg-link"
              data-test="institutions-toggle"
              [attr.aria-expanded]="showAllInstitutions"
              (click)="showAllInstitutions = !showAllInstitutions"
            >
              {{ showAllInstitutions ? 'Ver solo las 5 de mayor total' : 'Ver las ' + institutionsCount + ' instituciones' }}
            </button>
          </div>

          <!-- RUG con registros -->
          <div class="alg-block span-all" *ngIf="!sectionState(sections.securedLoans)" data-test="rug">
            <div class="alg-sec"><h3>RUG · garantías mobiliarias</h3></div>
            <div class="alg-table-wrap">
              <table class="alg-table rug-table">
                <caption class="p-hidden-accessible">
                  Garantías inscritas en el Registro Único de Garantías Mobiliarias
                </caption>
                <colgroup>
                  <col style="width: 44px" />
                  <col style="width: 150px" />
                  <col class="is-fill-creditor" />
                  <col class="is-fill-type" />
                  <col style="width: 120px" />
                  <col style="width: 110px" />
                </colgroup>
                <thead>
                  <tr>
                    <th scope="col"><span class="p-hidden-accessible">Detalle</span></th>
                    <th scope="col">Garantía</th>
                    <th scope="col">Acreedor</th>
                    <th scope="col">Tipo</th>
                    <th scope="col" class="is-num">Monto</th>
                    <th scope="col">Vigencia</th>
                  </tr>
                </thead>
                <tbody>
                  <ng-container *ngFor="let loan of sections.securedLoans?.data?.items ?? []; let i = index; trackBy: trackByIndex">
                    <tr data-test="rug-row" [class.is-open]="openRows.has('rug:' + i)">
                      <td>
                        <button
                          type="button"
                          class="alg-exp"
                          data-test="rug-toggle"
                          [attr.aria-expanded]="openRows.has('rug:' + i)"
                          [attr.aria-label]="'Ver los bienes de la garantía ' + (loan.guaranteeNumber ?? '')"
                          (click)="toggleRow('rug:' + i)"
                        >
                          <i class="fa-solid fa-chevron-right" aria-hidden="true"></i>
                        </button>
                      </td>
                      <td>
                        <span class="alg-mono">{{ loan.guaranteeNumber ?? '—' }}</span>
                        <span class="alg-table__sub">Inscrita {{ shortDate(loan.registeredAt) }}</span>
                        <span class="alg-pill is-neutral" *ngIf="loan.cancelledAt">Cancelada</span>
                      </td>
                      <td>{{ loan.creditor ?? '—' }}</td>
                      <td>
                        {{ humanize(loan.guaranteeType) }}
                        <!-- Resumen de los bienes en una línea; la descripción completa y sus categorías van en el detalle. -->
                        <span
                          class="alg-table__sub clamp-1"
                          *ngIf="loan.assetDescription"
                          [attr.title]="loan.assetDescription"
                          data-test="rug-assets-summary"
                          >{{ loan.assetDescription }}</span
                        >
                      </td>
                      <td class="is-num">{{ loan.amount !== null ? money(loan.amount) : 'Sin monto' }}</td>
                      <td>{{ securedLoanValidity(loan) }}</td>
                    </tr>
                    <tr class="alg-detail" *ngIf="openRows.has('rug:' + i)" data-test="rug-detail">
                      <td colspan="6">
                        <div class="alg-detail__grid rug-detail">
                          <div>
                            <div class="alg-lbl">Bienes</div>
                            <div class="alg-detail__value">{{ loan.assetDescription ?? '—' }}</div>
                          </div>
                          <div>
                            <div class="alg-lbl">Categorías</div>
                            <div class="alg-detail__value">
                              {{ loan.assetTypes.length ? loan.assetTypes.map(humanize).join(' · ') : '—' }}
                            </div>
                          </div>
                        </div>
                      </td>
                    </tr>
                  </ng-container>
                </tbody>
              </table>
            </div>
          </div>

          <!-- Infonavit con registros: sin forma fija, etiqueta y valor -->
          <div class="alg-box span-all" *ngIf="!sectionState(sections.infonavit)" data-test="infonavit">
            <div class="alg-sec"><h3>Infonavit</h3></div>
            <dl class="record-fields" *ngFor="let record of sections.infonavit?.data?.items ?? []; trackBy: trackByIndex">
              <div *ngFor="let field of entries(record); trackBy: trackByIndex" data-test="infonavit-field">
                <dt class="alg-lbl">{{ field.key }}</dt>
                <dd class="alg-detail__value">{{ field.value }}</dd>
              </div>
            </dl>
          </div>
        </div>

        <!-- ─── Lo que no trae registros, con su estado ─── -->
        <div class="alg-none" *ngIf="sourcesWithoutRecords.length" data-test="without-records">
          <span class="alg-none__label">Sin registros:</span>
          <span
            *ngFor="let source of sourcesWithoutRecords; trackBy: trackByIndex"
            class="alg-src"
            [attr.data-test]="source.key === 'securedLoans' ? 'rug' : source.key === 'infonavit' ? 'infonavit' : 'source-' + source.key"
            [attr.data-state]="source.state"
            [attr.title]="source.text"
          >
            <i
              aria-hidden="true"
              [ngClass]="
                source.state === 'empty'
                  ? 'fa-solid fa-check is-ok'
                  : source.state === 'pending'
                    ? 'fa-regular fa-clock is-warn'
                    : source.state === 'source-error'
                      ? 'fa-solid fa-triangle-exclamation is-bad'
                      : 'fa-solid fa-minus is-muted'
              "
            ></i
            >{{ source.label }}<span class="p-hidden-accessible">: {{ source.text }}</span>
          </span>
        </div>
      </div>
    </article>
  </ng-template>
</section>
